import { getApprovalRequest } from '@gonfalon/openapi/operations/getApprovalRequest';
import {
  getApprovalRequests,
  type GetApprovalRequestsQueryParams,
} from '@gonfalon/openapi/operations/getApprovalRequests';
import { patchApprovalRequest } from '@gonfalon/openapi/operations/patchApprovalRequest';
import { getQueryClient } from '@gonfalon/react-query-client/getQueryClient';
import { environment, flag, project } from '@gonfalon/resource-specifiers/ast';
import { createQueryKeys } from '@lukemorales/query-key-factory';
import { mergeHeaders } from 'openapi-fetch';

import { createMutationHook } from '../internal/createMutationHook';
import { createQueryOptions } from '../internal/createQueryOptions';
import { reactQueryResponseAdapter } from '../internal/reactQueryResponseAdapter';

/**
 * @deprecated Use `createQueryOptions` with the OpenAPI operation directly instead.
 * See packages/rest-api/README.md for the recommended pattern.
 */
export const approvalRequests = createQueryKeys('approvalRequests', {
  list: ({ params }: { params?: GetApprovalRequestsQueryParams }) => ({
    queryKey: params ? ['filtered', params] : ['all'],
    queryFn: async ({ signal }) => reactQueryResponseAdapter(getApprovalRequests({ query: params }, { signal })),
  }),
  detail: ({ approvalRequestId }: { approvalRequestId: string }) => ({
    queryKey: [{ approvalRequestId }],
    queryFn: async ({ signal }) => reactQueryResponseAdapter(getApprovalRequest({ id: approvalRequestId }, { signal })),
  }),
});

export const approvalRequestsList = approvalRequests.list;
export const approvalRequestsDetail = approvalRequests.detail;

/**
 * Every approval request on a flag that still needs action: any review status combined with a
 * `pending` workflow status.
 */
const PENDING_APPROVAL_FILTER: {
  reviewStatus: Array<'pending' | 'approved' | 'declined'>;
  status: Array<'pending'>;
} = {
  reviewStatus: ['pending', 'approved', 'declined'],
  status: ['pending'],
};

/**
 * Pending approval requests for a flag in a single environment.
 *
 * Deliberately requests no `expand`. Callers that need the environment should use the one they
 * already hold, since `resourceId` pins the query to a single environment. Expanding `resource`
 * in particular is not free — it makes the server run conflict checking per approval request.
 *
 * `resourceKind` is not the kind filter it looks like. On the flag path it compiles to the same
 * predicate `resourceId` already emits, so it does not separate flag-config approvals from
 * expiring-target or scheduled-change ones. What it does do is suppress results from the V2
 * approvals store, whose kinds (segment, aiconfig, aiconfig-targeting, experiment) never equal
 * `flag`. This query always sends it, matching the legacy flag call sites, so V2-store approvals
 * never appear on flag surfaces served by this query.
 */

async function internalGetPendingFlagApprovalRequests(
  {
    projectKey,
    flagKey,
    environmentKey,
    silenceRateLimits = false,
  }: {
    projectKey: string;
    flagKey: string;
    environmentKey: string;
    // Temporary: when true, 429 responses resolve as empty data instead of throwing.
    // This preserves the pre-adapter silent-failure behavior for rate limits while the
    // fix-pending-changes-429 flag is rolled out. It is part of the query key (createQueryOptions
    // spreads the input into it), so when the flag flips mid-session callers move to a fresh
    // entry instead of reusing a silenced-era empty success. Remove with the flag cleanup.
    silenceRateLimits?: boolean;
  },
  context: { signal: AbortSignal },
) {
  const result = await getApprovalRequests(
    {
      query: {
        filter: {
          resourceId: flag(environment(project(projectKey), environmentKey), flagKey),
          resourceKind: 'flag',
          ...PENDING_APPROVAL_FILTER,
        },
      },
    },
    context,
  );

  if (silenceRateLimits && result.response.status === 429) {
    // Temporary: a synthetic OK response makes the adapter resolve an empty page instead of
    // throwing. Every caller guards with `data?.items ?? []`. Remove when
    // fix-pending-changes-429 is cleaned up.
    return {
      data: { items: [] } as unknown as NonNullable<typeof result.data>,
      response: new Response(null, { status: 200 }),
    } as typeof result;
  }

  return result;
}

/**
 * Pending approval requests for a flag in a single environment. Shared by the legacy sidebar, the
 * legacy pending-changes button, and the flag-targeting rewrite so one fetch serves all surfaces.
 * Invalidate with `pendingFlagApprovalRequestsQuery.partialQueryKey(...)` after approval writes.
 */
export const pendingFlagApprovalRequestsQuery = createQueryOptions(internalGetPendingFlagApprovalRequests);

function patchApprovalRequestSemantically(
  input: Parameters<typeof patchApprovalRequest>[0],
  options: Parameters<typeof patchApprovalRequest>[1] = {},
) {
  return patchApprovalRequest(input, {
    ...options,
    headers: mergeHeaders(options.headers, {
      'Content-Type': 'application/json; domain-model=launchdarkly.semanticpatch',
    }),
  });
}

export const useUpdateApprovalRequestSemantically = createMutationHook(patchApprovalRequestSemantically, {
  onSuccess: async () => {
    const queryClient = getQueryClient();
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: approvalRequests._def }),
      queryClient.invalidateQueries({ queryKey: pendingFlagApprovalRequestsQuery.partialQueryKey() }),
    ]);
  },
});
